import React, { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../theme';

// ============================================================================
// BADGE VARIANTS
// ============================================================================

const badgeVariants = cva(
  [
    'inline-flex items-center justify-center',
    'rounded-full font-medium',
    'transition-colors',
  ],
  {
    variants: {
      variant: {
        default: 'bg-cream text-navy-light',
        primary: 'bg-sage text-white',
        secondary: 'bg-cream-dark text-navy',
        success: 'bg-success/15 text-success',
        warning: 'bg-terracotta/15 text-terracotta',
        error: 'bg-red-500/15 text-red-600',
        info: 'bg-blue-500/15 text-blue-600',
        outline: 'border border-warm-gray/30 text-navy-light bg-transparent',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

// ============================================================================
// BADGE COMPONENT
// ============================================================================

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Optional icon to display before the badge text.
   */
  icon?: React.ReactNode;
}

/**
 * Badge component for displaying status indicators, labels, or counts.
 * 
 * @example
 * // Default badge
 * <Badge>New</Badge>
 * 
 * // Status badge
 * <Badge variant="success">Active</Badge>
 * 
 * // With icon
 * <Badge variant="info" icon={<Info className="h-3 w-3" />}>Info</Badge>
 */
const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, icon, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {icon && <span className="mr-1 -ml-0.5">{icon}</span>}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// ============================================================================
// EXPORTS
// ============================================================================

export { Badge, badgeVariants };
